import { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row } from 'antd';
import { observer } from 'mobx-react-lite';

import { useResource } from 'app/common/api';
import { AppPage } from 'app/common/app-page/features/app-page/app-page';
import {
  NotificationsProvider,
  useNotifyFactory,
} from 'app/common/notifications/features/notifications';
import { createJsonComponent } from 'app/libs/json-components/create-json-component';
import { WagonDataProvider } from 'app/libs/json-components/wagon-data-provider';
import { Loading, PageContent } from 'app/ui';

import { getWagonRequest } from '../requests/get-wagon.request';

const wagonSchemeIds = [1, 2, 3, 4, 5, 6] as const;

export const WagonSchemesPage = observer(() => {
  const pageNotify = useNotifyFactory();
  const [wagonSchemes, setWagonSchemes] = useState({
    1: null,
    2: null,
    3: null,
    4: null,
    5: null,
    6: null,
  });

  const getWagonScheme = useResource<number, any>({
    request: getWagonRequest,
    errorNotify: pageNotify,
    onDone: (wagonSchema) => {
      setWagonSchemes((prev) => ({ ...prev, [wagonSchema.id]: wagonSchema }));
    },
  });

  useEffect(() => {
    wagonSchemeIds.forEach((wagonId) => {
      getWagonScheme.send(wagonId);
    });
  }, []);

  return (
    <NotificationsProvider value={pageNotify}>
      <AppPage perm="*">
        <PageContent>
          <Loading active={getWagonScheme.isLoading}>
            <Row gutter={[16, 16]}>
              {wagonSchemeIds.map((wagonId) => {
                const wagonSchema = wagonSchemes[wagonId];

                if (wagonSchema === null) return null;

                return (
                  <Col
                    xxl={6}
                    xl={8}
                    lg={12}
                    md={24}
                    sm={24}
                    xs={24}
                    key={wagonId}
                  >
                    <WagonSchemaCard schema={wagonSchema} />
                  </Col>
                );
              })}
            </Row>
          </Loading>
        </PageContent>
      </AppPage>
    </NotificationsProvider>
  );
});

const WagonSchemaCard = observer(
  ({ schema }: { schema: Record<string, any> }) => {
    return (
      <Card size="small" title={`Вагон ${schema.id}`}>
        <WagonPainter scheme={schema} direction="backward" />
      </Card>
    );
  },
);

type WagonPainterProps = {
  scheme: Record<string, any>;
  direction: 'forward' | 'backward';
};

const WagonPainter = observer(({ scheme, direction }: WagonPainterProps) => {
  const WagonComponent = useMemo(() => {
    if (!scheme) return () => null;

    return () => createJsonComponent(scheme.schema);
  }, [scheme]);

  return (
    <WagonDataProvider
      direction={direction}
      // @ts-expect-error ...
      scheme={scheme}
      seats={[]}
      disabled={false}
      value={[]}
    >
      <div>
        <WagonComponent />
      </div>
    </WagonDataProvider>
  );
});
